import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  Controller,
  ControllerRenderProps,
  useFieldArray,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../../app/hooks";
import CommonTextField from "../../../../../../common/components/functional/commonTextField/CommonTextField";
import { ReactComponent as DeleteIcon } from "../../../../../../common/icons/delete.svg";
import { ReactComponent as DeleteIconDisabled } from "../../../../../../common/icons/deleteDark.svg";
import { selectAssignmentOptionsByEmploymentId } from "../../../AssignmentAdditionalInfo/AssignmentsSlice";
import { InternalStateAccumulationsFormValues } from "../../types";

type Props = {
  headers: any[];
  renderField: (
    subKey: string,
    field: ControllerRenderProps<InternalStateAccumulationsFormValues, any>,
  ) => JSX.Element;
  isInitializing: boolean;
  includesAssignmentId: boolean;
  employmentId: string;
};

const AccumulationMultiCalculationTable = ({
  headers,
  renderField,
  isInitializing,
  includesAssignmentId,
  employmentId,
}: Props) => {
  const { t } = useTranslation("talentProfile");

  const assignmentOptionsByEmploymentId = useAppSelector(
    selectAssignmentOptionsByEmploymentId(employmentId),
  );

  const { control, watch, getValues } =
    useFormContext<InternalStateAccumulationsFormValues>();
  const fields = watch("rows");

  const { update, remove } = useFieldArray({
    control,
    rules: { required: true },
    name: "rows",
  });

  const allowedFields = ["workTime", "salary", "valueDate"];

  const handleAddRow = () => {
    const currentValues = getValues();

    const newRow = {
      // TEMP_KEY will be replaced with null or "1970-01-01" by comparing the valueDate of the rows
      // see onSave in AccumulationsEditTable.tsx
      TEMP_KEY: {
        valueDate: "",
        salary: "",
        workTime: "",
      },
    };

    update(currentValues.rows.length, newRow);
  };

  const handleRemoveRow = (index: number) => remove(index);

  const disableAddRow = fields.length === 2 || !isInitializing;

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("accumulationKeys.modificationDate")}</TableCell>
            {includesAssignmentId && (
              <TableCell>{t("accumulationKeys.assignmentId")}</TableCell>
            )}
            {headers.map(([mapKeyDate, fieldData]) =>
              Object.keys(fieldData).map((subKey) =>
                allowedFields.includes(subKey) ? (
                  <TableCell key={`${mapKeyDate}-${subKey}`}>
                    {/* different translations with the same key ->
                      needs more efficient solution if more keys are added
                  */}
                    {subKey === "workTime"
                      ? t(`accumulationKeys.workTimeMulti`)
                      : t(`accumulationKeys.${subKey}`)}
                  </TableCell>
                ) : (
                  <></>
                ),
              ),
            )}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {/* TODO: move fields to renderField */}
            <TableCell>
              <Controller
                name="modificationDate"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DesktopDatePicker
                    {...field}
                    format="dd.MM.yyyy"
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                  />
                )}
              />
            </TableCell>
            {includesAssignmentId && (
              <TableCell>
                <Controller
                  name="assignmentId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      options={assignmentOptionsByEmploymentId}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={field.value || null}
                      onChange={(_, newValue) => field.onChange(newValue)}
                      renderInput={(params: AutocompleteRenderInputParams) => (
                        <CommonTextField {...params} sx={{ width: "300px" }} />
                      )}
                    />
                  )}
                />
              </TableCell>
            )}
            <TableCell colSpan={13} />
          </TableRow>
          {fields.flatMap((field, index) => (
            <TableRow key={index}>
              <TableCell />
              {includesAssignmentId && <TableCell />}
              {Object.entries(field).map(
                ([key, fieldData]) =>
                  key !== "id" &&
                  Object.entries(fieldData).map(([subKey, value]) => (
                    <TableCell key={`${key}-${subKey}`}>
                      <Controller
                        name={`rows.${index}.${key}.${subKey}`}
                        rules={{ required: true }}
                        control={control}
                        render={({ field }) =>
                          renderField(subKey, { ...field })
                        }
                      />
                    </TableCell>
                  )),
              )}
              <TableCell>
                <IconButton
                  disabled={!isInitializing}
                  onClick={() => handleRemoveRow(index)}
                >
                  {!isInitializing ? <DeleteIconDisabled /> : <DeleteIcon />}
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell
              sx={{ border: "none", padding: "4px 16px" }}
              align="right"
              colSpan={13}
            >
              <Button
                disabled={disableAddRow}
                onClick={handleAddRow}
                startIcon={<AddIcon />}
              >
                {t("talentProfile:addRow")}
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AccumulationMultiCalculationTable;
